import React, { useEffect, useRef, useState } from "react";

type ProductVideoProps = {
  className?: string;
  videoClassName?: string;
};

const ProductVideo: React.FC<ProductVideoProps> = ({
  className = "",
  videoClassName = "",
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [canPlay, setCanPlay] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const playPromise = video.play();
    if (playPromise) {
      playPromise.catch(() => setIsPlaying(false));
    }
  }, []);

  const togglePlayback = async () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      await video.play();
      setIsPlaying(true);
      return;
    }

    video.pause();
    setIsPlaying(false);
  };

  return (
    <div
      className={`group relative aspect-video overflow-hidden rounded-[1.35rem] bg-slate-950 shadow-2xl ring-1 ring-white/20 ${className}`}
    >
      <div className="absolute left-1/2 top-2 z-10 h-1.5 w-16 -translate-x-1/2 rounded-full bg-white/20" />
      <video
        ref={videoRef}
        poster="/assets/desktop-sale-page.png"
        className={`block h-full w-full object-cover ${videoClassName}`}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        onCanPlay={() => setCanPlay(true)}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      >
        {/* Prefer compressed web formats when available; keep mov as fallback. */}
        <source src="/assets/hovastore.webm" type="video/webm" />
        <source src="/assets/hovastore.mp4" type="video/mp4" />
        <source src="/assets/hovastore.mov" type="video/quicktime" />
      </video>
      {!canPlay ? (
        <div className="pointer-events-none absolute inset-0 animate-pulse bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
      ) : null}
      <button
        type="button"
        onClick={togglePlayback}
        aria-label={isPlaying ? "Pause HovaStore video" : "Play HovaStore video"}
        className="absolute bottom-4 right-4 flex h-12 w-12 items-center justify-center rounded-full bg-white/95 text-primary shadow-lg ring-1 ring-black/10 transition hover:scale-105 hover:bg-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-primary"
      >
        {isPlaying ? (
          <span className="flex h-5 w-5 items-center justify-center gap-1">
            <span className="h-5 w-1.5 rounded-sm bg-current" />
            <span className="h-5 w-1.5 rounded-sm bg-current" />
          </span>
        ) : (
          <span className="ml-1 h-0 w-0 border-y-[10px] border-l-[16px] border-y-transparent border-l-current" />
        )}
      </button>
    </div>
  );
};

export default ProductVideo;
